import React, { Fragment, useRef } from 'react';

// Components

import AlertInfo from '../../components/common/alert/AlertInfo';
import InstructionalText from '../../components/ssap/notifications/InstructionalText';

// Utils

import ConditionalRendering from '../../components/common/conditional-rendering/ConditionalRendering';
import { normalizeStringToBooleanValue } from '../../utils/normalize/normalizeStringToBooleanValue';

const RadioButtons = props => {
    const {
        additionalContent = null,
        inputData: {
            id,
            inputClass,
            liClass,
            managingValues = {},
            options,
            optionsReplacements,
            returnAsNumber,
            type,
            value
        },
        label: { labelClass, ulClass, showRequiredIcon, isLabelVisible = true },
        legend: {
            dataInstructions,
            dataInstructionsAnchor: anchor,
            dataInstructionsRightSide,
            replacements,
            legendText,
            instructionsViaPortal: [isPortal, portalMargin] = [],
            onlyInstructions,
        },
    } = props;

    const handleValueChange = e => {
        const {
            dataset: { value },
        } = e.currentTarget;
        props.inputActions.handleFieldChange(normalizeStringToBooleanValue(value, returnAsNumber));
    };

    const keyPress = e => {
        const isEnterOrSpace =
            e.key === ' ' || e.key === 'SpaceBar' || e.key === 'Enter' || e.keyCode === 32 || e.keyCode === 13;
        if (isEnterOrSpace) {
            handleValueChange(e);
        }
    };

    const supportNullValueForOption = (option = {}) => {
        return option.value === null ? 'null' : option.value;
    };

    const wrapWithComponent = (componentName, textId, key) => {
        const supportedComponents = {
            'AlertInfo': {
                Component: AlertInfo,
                key,
                id: textId,
                textReplacements: optionsReplacements,
                skipIntl: true
            }
        };

        const {
            Component,
            ...restOfPropsForWrapper
        } = supportedComponents[componentName];

        return (
            <Component { ...restOfPropsForWrapper } />
        );
    };

    // If it is needed multi-location react portal anchor may be implemented.
    // For a while there are only 2 usages for it (here and in Checkbox)
    const reactPortalAnchor = useRef();

    return (
        <Fragment>
            <div className={`grid-row gi-component gi-${type}`}>
                <fieldset className="usa-fieldset">
                    <ConditionalRendering isTrue={isLabelVisible}>
                        <legend className={`usa-legend ${labelClass && labelClass.join(' ')}`}>
                            <span className={`required-${showRequiredIcon}`}>{legendText}</span>
                            {dataInstructions && (
                                <InstructionalText
                                    anchor={anchor}
                                    Component={<span data-instructions={dataInstructions} />}
                                    onlyInstructions={onlyInstructions}
                                    replacements={replacements}
                                    reactPortalAnchor={isPortal ? reactPortalAnchor : { current: null }}
                                />
                            )}
                        </legend>
                    </ConditionalRendering>

                    {additionalContent}

                    <ul className={`usa-list usa-list--unstyled ${ulClass}`}>
                        {options.map((option, i) => {
                            const {
                                asComponent,
                                isInputDisabled,
                                isInputShown,
                                isOnlyLabelShown,
                                optionValue
                            } = typeof option.value === 'function'
                                ? option.value(managingValues)
                                : {
                                    asComponent: null,
                                    isInputDisabled: option.disabled,
                                    isInputShown: true,
                                    isOnlyLabelShown: false,
                                    optionValue: option.value,
                                };

                            const key = `${ option.optionId }_${ i }}`;

                            if (isOnlyLabelShown) {
                                if(asComponent) {
                                    return wrapWithComponent(asComponent, option.text, key);
                                } else {
                                    return (
                                        <li key = { key }>
                                            { option.text }
                                        </li>
                                    );
                                }
                            }

                            if (isInputShown) {
                                return (
                                    <li key={`${option.text}_${i}`} className={`${liClass && liClass.join(' ')}`}>
                                        <input
                                            id={`${id}_option${i}`}
                                            className={`usa-radio__input gi-radio_buttons__input ${
                                                inputClass && inputClass.join(' ')
                                            }`}
                                            type="radio"
                                            checked={optionValue === value}
                                            tabIndex="0"
                                            role="radio"
                                            aria-checked={optionValue === value}
                                            data-value={supportNullValueForOption({
                                                ...option,
                                                value: optionValue,
                                            })}
                                            aria-labelledby={`${id}_label${i}`}
                                            onKeyPress={keyPress}
                                            onChange={() => {}}
                                            disabled={isInputDisabled || false}
                                        />
                                        <label
                                            className="usa-radio__label gi-radio_buttons__label"
                                            id={`${id}_label${i}`}
                                            data-testid={`${id}_option_${option.optionId}`}
                                            htmlFor={`${id}_option${i}`}
                                            onClick={isInputDisabled ? () => {} : handleValueChange}
                                            data-value={supportNullValueForOption({
                                                ...option,
                                                value: optionValue,
                                            })}
                                        >
                                            {option.text}
                                        </label>
                                    </li>
                                );
                            }
                        })}
                        {dataInstructionsRightSide && (
                            <InstructionalText
                                anchor={anchor}
                                expandClassName="margin-t-10"
                                Component={<span data-instructions={dataInstructionsRightSide} />}
                            />
                        )}
                    </ul>
                    <div ref={reactPortalAnchor} style={{ marginTop: portalMargin }} />
                </fieldset>
            </div>
        </Fragment>
    );
};

export default RadioButtons;
